/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
/* eslint-disable sonarjs/cognitive-complexity */
'use client';
import { Chevron, Close } from '@ahamove/icons';
import { Path, Pickup } from '@ahamove/icons/map';
import { IndexedPath as IconIndexedPath } from '@ahamove/icons/marker';
import { formatCurrency } from '@ahm/common-helpers';
import { useLocationStore } from '@ahm/react-map/store';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ActionIcon,
  Avatar,
  Box,
  Button,
  Card,
  Group,
  rem,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import type { ContextModalProps } from '@mantine/modals';
import { cloneDeep } from 'lodash';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import QuestionIcon from '@/assets/icons/question.svg';
import ModalFooter from '@/components/modals/components/modal-footer';
import ModalWrapper from '@/components/modals/modal-wrapper';
import LoadingGoodsItem from '@/components/modals/special-request-modal/components/loading-goods-item';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import { useLocaleSuffix } from '@/navigation';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

export type LoadingGoodsModalProps = ContextModalProps<{
  request?: {
    _id: string;
    options: ServiceOption[];
    name_vi_vn?: string;
  };
}>;

interface OptionValue {
  code: string;
  name_vi_vn: string;
  name_en_us: string;
  default?: boolean;
  price: number;
  short_name_vi_vn: string;
  short_name_en_us: string;
  disable_rules?: string[];
}

interface ServiceOption {
  code: string;
  name_vi_vn: string;
  name_en_us: string;
  type: 'RADIO' | 'CHECKBOX';
  description_vi_vn?: string;
  description_en_us?: string;
  values: OptionValue[];
  price: number;
  short_name_vi_vn: string;
  short_name_en_us: string;
}

// Update distance options to use data from API
const getBocXepRangesOptions = (options: ServiceOption[]) => {
  const bocXepRanges = options.find(
    (option) => option.code === 'BOCXEP_RANGES'
  );
  if (!bocXepRanges) return [];
  return bocXepRanges.values.map((value) => ({
    value: value.code,
    label: value.name_vi_vn,
    labelEn: value.name_en_us,
    price: value.price,
    default: value.default,
    shortName: value.short_name_vi_vn,
    shortNameEn: value.short_name_en_us,
    disableRules: value.disable_rules || [],
  }));
};

// Add a helper function to extract checkbox options
const getCheckboxOptions = (options: ServiceOption[]) => {
  return options
    .filter((option) => option.type === 'CHECKBOX')
    .map((option) => ({
      code: option?.code,
      name: option?.name_vi_vn,
      nameEn: option?.name_en_us,
      descVn: option?.description_vi_vn,
      descEn: option?.description_en_us,
      price: option?.price,
      values: option?.values || [],
    }));
};

// Update type to use option codes instead of hardcoded values
type DistanceOption = string;

export default function LoadingGoodsModal({
  context,
  id,
  innerProps,
}: LoadingGoodsModalProps) {
  const { request } = innerProps;
  const options = request?.options || [];
  const isMobile = useIsMobile();
  const tActions = useTranslations('common.actions');

  const allPaths = useLocationStore.use.paths();
  const pickupPath = allPaths[0];
  const dropOffPath = allPaths.slice(1); // Exclude paths[0]
  const { selectedService, serviceRequests, setServiceRequest } =
    useBookingStore((state) => state);

  const localeSuffix = useLocaleSuffix();

  const [opened, { open, close }] = useDisclosure(false);
  // Add state to track which type is being edited in the modal
  const [editingType, setEditingType] = useState<
    'pickupHelperConfig' | 'deliveryHelperConfig' | null
  >(null);

  // State tạm để lưu dữ liệu khi đang chỉnh sửa trong modal mobile
  const [tempConfig, setTempConfig] = useState<{
    distance: string;
    additionalOptions: Record<string, boolean>;
  } | null>(null);

  const schema = z.object({
    pickupHelperConfig: z.object({
      distance: z.string(),
      additionalOptions: z.record(z.string(), z.boolean()).optional(),
    }),
    deliveryHelperConfig: z.object({
      distance: z.string(),
      additionalOptions: z.record(z.string(), z.boolean()).optional(),
    }),
  });

  type FormData = z.infer<typeof schema>;

  const {
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { isValid },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      pickupHelperConfig: {
        distance: '',
        additionalOptions: {},
      },
      deliveryHelperConfig: {
        distance: '',
        additionalOptions: {},
      },
    },
  });
  const pickupHelperConfig = watch('pickupHelperConfig');
  const deliveryHelperConfig = watch('deliveryHelperConfig');

  const DISTANCE_OPTIONS = getBocXepRangesOptions(options);
  const CHECKBOX_OPTIONS = getCheckboxOptions(options);

  const handleDistanceChange = (
    value: DistanceOption,
    type: 'pickupHelperConfig' | 'deliveryHelperConfig'
  ) => {
    const currentDistance = watch(`${type}.distance`);

    // Nếu user click lại đúng distance đang chọn -> reset hết
    if (value === '' || value === currentDistance) {
      setValue(type, {
        distance: '',
        additionalOptions: {},
      });
      return;
    }

    const findDistanceOption = DISTANCE_OPTIONS.find(
      (item) => item.value === value
    );
    const currentOptions = watch(`${type}.additionalOptions`) || {};

    // Apply disable rules khi chọn distance mới
    if (findDistanceOption?.disableRules?.length) {
      for (const key of findDistanceOption.disableRules) {
        if (key in currentOptions) {
          delete currentOptions[key];
        }
      }
    }

    setValue(type, {
      distance: value,
      additionalOptions: { ...currentOptions },
    });
  };

  // Handle checkbox option change
  const handleCheckboxChange = (
    optionCode: string,
    type: 'pickupHelperConfig' | 'deliveryHelperConfig'
  ) => {
    const currentOptions = watch(`${type}.additionalOptions`) || {};
    const checked = !currentOptions[optionCode];
    setValue(`${type}.additionalOptions` as const, {
      ...currentOptions,
      [optionCode]: Boolean(checked),
    });
  };

  const formatHelperOptions = (config: any, pathIndex: number) => {
    if (!config.distance || !request?._id) return null;

    type OptionType = { code: string; selected_value?: string };
    const options: OptionType[] = [];

    // Add the selected distance option
    options.push({
      code: 'BOCXEP_RANGES',
      selected_value: config.distance,
    });

    const additionalOptions = config.additionalOptions || {};
    Object.entries(additionalOptions).forEach(([optionCode, isSelected]) => {
      if (isSelected) {
        options.push({ code: optionCode });
      }
    });

    return {
      _id: request._id,
      options,
      path_index: pathIndex,
      num: 1,
      value: 0,
    };
  };

  const onSubmit = (data: FormData) => {
    if (!selectedService?._id || !request) return;
    const param = {
      ...serviceRequests,
    };
    // Define the type for loading goods options
    type LoadingGoodsOption = {
      _id: string;
      options: { code: string; selected_value?: string }[];
      path_index: number;
      num: number;
      value: number;
    };
    const loadingGoodsOptions: LoadingGoodsOption[] = [];

    // Format pickup helper config (path_index: 0)
    const pickupConfig = formatHelperOptions(data.pickupHelperConfig, 0);
    if (pickupConfig) {
      loadingGoodsOptions.push(pickupConfig);
    }

    // Format delivery helper configs for each delivery point
    if (data.deliveryHelperConfig.distance) {
      // Apply the same delivery helper config to all delivery paths
      dropOffPath.forEach((_, index) => {
        const deliveryConfig = formatHelperOptions(
          data.deliveryHelperConfig,
          index + 1 // Path indices for delivery points start from 1
        );
        if (deliveryConfig) {
          loadingGoodsOptions.push(deliveryConfig);
        }
      });
    }
    // Add the request to the service requests
    if (loadingGoodsOptions.length > 0) {
      // Cast the param to any to avoid type issues
      (param as any)[request._id] = loadingGoodsOptions;

      setServiceRequest(param);
    }

    // Close the modal
    context.closeModal(id);
  };

  // Update fee calculation to include checkbox options for a single delivery config
  const calculatedFees = () => {
    let total = 0;

    const pickupOption = DISTANCE_OPTIONS.find(
      (opt) => opt.value === pickupHelperConfig.distance
    );
    if (pickupOption) {
      total += pickupOption.price;
    }

    const pickupAdditionalOptions = pickupHelperConfig.additionalOptions || {};
    Object.entries(pickupAdditionalOptions).forEach(
      ([optionCode, isSelected]) => {
        if (isSelected) {
          const optionGroup = CHECKBOX_OPTIONS.find(
            (group) => group.code === optionCode
          );
          if (optionGroup?.price) {
            total += optionGroup.price;
          }
        }
      }
    );

    if (dropOffPath.length > 0) {
      const deliveryOption = DISTANCE_OPTIONS.find(
        (opt) => opt.value === deliveryHelperConfig.distance
      );
      if (deliveryOption) {
        total += deliveryOption.price * dropOffPath.length;
      }

      const deliveryAdditionalOptions =
        deliveryHelperConfig.additionalOptions || {};
      Object.entries(deliveryAdditionalOptions).forEach(
        ([optionCode, isSelected]) => {
          if (isSelected) {
            const optionGroup = CHECKBOX_OPTIONS.find(
              (group) => group.code === optionCode
            );
            if (optionGroup?.price) {
              total += optionGroup.price * dropOffPath.length;
            }
          }
        }
      );
    }

    return total;
  };

  // Add a helper function to check if an option should be disabled based on selected distance
  const isOptionDisabled = (optionCode: string, selectedDistance: string) => {
    const selectedOption = DISTANCE_OPTIONS.find(
      (option) => option.value === selectedDistance
    );
    return selectedOption?.disableRules?.includes(optionCode) || false;
  };

  // Hàm mở modal mobile với config tạm
  const handleOpenModalWithTemp = (
    type: 'pickupHelperConfig' | 'deliveryHelperConfig'
  ) => {
    setEditingType(type);
    const currentConfig =
      type === 'pickupHelperConfig' ? pickupHelperConfig : deliveryHelperConfig;
    setTempConfig(cloneDeep(currentConfig));
    open();
  };

  // Hàm xác nhận trong modal mobile - lưu config tạm vào form
  const handleConfirmModalConfig = () => {
    if (editingType && tempConfig) {
      // When unchecking a distance (empty string) and pressing confirm, clear all options
      if (tempConfig.distance === '') {
        // Clear all options by setting an empty configuration
        setValue(editingType, {
          distance: '',
          additionalOptions: {},
        });
      } else {
        // Normal flow - save the temp config to the form
        setValue(editingType, tempConfig);
      }
    }
    close();
  };

  const handleCloseModal = () => {
    close();
  };

  const handleTempDistanceChange = (value: string) => {
    if (!tempConfig) return;

    const currentDistance = tempConfig.distance;

    // Nếu click vào lại distance đang chọn -> reset hết
    if (value === '' || value === currentDistance) {
      setTempConfig({
        distance: '',
        additionalOptions: {},
      });
      return;
    }

    // Xử lý disable rules khi chọn distance mới
    const findDistanceOption = DISTANCE_OPTIONS.find(
      (item) => item.value === value
    );
    const currentOptions = { ...tempConfig.additionalOptions };

    if (findDistanceOption?.disableRules?.length) {
      for (const key of findDistanceOption.disableRules) {
        if (key in currentOptions) {
          delete currentOptions[key];
        }
      }
    }

    setTempConfig({
      ...tempConfig,
      distance: value,
      additionalOptions: currentOptions,
    });
  };

  // Hàm thay đổi checkbox trong config tạm
  const handleTempCheckboxChange = (optionCode: string) => {
    if (!tempConfig) return;

    const currentValue = tempConfig.additionalOptions?.[optionCode] || false;
    setTempConfig({
      ...tempConfig,
      additionalOptions: {
        ...tempConfig.additionalOptions,
        [optionCode]: !currentValue,
      },
    });
  };

  if (!request) return null;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {isMobile ? (
        <>
          <Group
            bg={'white'}
            w="100%"
            align="center"
            justify="space-between"
            p={16}
            pos="sticky"
            top={0}
            left={0}
            styles={{
              root: {
                zIndex: 100,
                borderBottom: `1px solid var(--mantine-color-neutral-1)`,
              },
            }}
          >
            <ActionIcon
              size={rem(24)}
              variant="transparent"
              onClick={() => context.closeModal(id)}
            >
              <Close color="black" width={24} height={24} />
            </ActionIcon>
            <Title order={3}>{request?.name_vi_vn ?? 'Dịch vụ bốc xếp'}</Title>

            <ActionIcon
              size={rem(24)}
              variant="transparent"
              onClick={() => context.closeModal(id)}
            >
              <QuestionIcon color="black" width={24} height={24} />
            </ActionIcon>
          </Group>
          <Group
            bg="white"
            p={16}
            w="100%"
            align="center"
            justify="space-between"
          >
            <Stack gap={4}>
              <Title order={5}>Chọn hình thức bốc xếp</Title>
              <Text lineClamp={2} c={'neutral.7'} size="sm">
                {request[`description_${localeSuffix}`]}
              </Text>
            </Stack>
            <Avatar
              radius="xl"
              size={rem(64)}
              src={request?.icon_url}
              alt={request?.name_vi_vn ?? 'Loading Service'}
            />
          </Group>

          {pickupPath && (
            <Group
              onClick={() => handleOpenModalWithTemp('pickupHelperConfig')}
              justify="flex-start"
              align="flex-start"
              bg="white"
              mt={8}
              p={rem(16)}
              w="100%"
              wrap="nowrap"
            >
              <Stack justify="flex-start">
                <Pickup width={16} height={16} />
              </Stack>
              <Stack gap={4} flex={1}>
                <Text lh="16px" size="md" fw={700}>
                  Điểm lấy hàng
                </Text>
                <Text lineClamp={1} c="neutral.5" size="sm" fw={500}>
                  {pickupPath.address}
                </Text>
              </Stack>
              <Stack justify="flex-start">
                <Chevron width={24} height={24} />
              </Stack>
            </Group>
          )}
          {dropOffPath?.length === 1 ? (
            <Group
              onClick={() => handleOpenModalWithTemp('deliveryHelperConfig')}
              justify="flex-start"
              bg="white"
              mt={8}
              p={rem(16)}
              w="100%"
              align="center"
              wrap="nowrap"
            >
              <Stack justify="flex-start">
                <IconIndexedPath value={0} width={16} height={16} />
              </Stack>
              <Stack gap={4} flex={1}>
                <Text lh="16px" size="md" fw={700}>
                  Điểm giao hàng
                </Text>
                <Text lineClamp={1} c="neutral.5" size="sm" fw={500}>
                  {dropOffPath[0].address}
                </Text>
              </Stack>
              <Stack justify="flex-start">
                <Chevron width={24} height={24} />
              </Stack>
            </Group>
          ) : dropOffPath?.length > 1 ? (
            <Box
              onClick={() => handleOpenModalWithTemp('deliveryHelperConfig')}
              bg="white"
              mt={8}
              p={rem(16)}
              w="100%"
              style={{ borderRadius: rem(12) }}
            >
              <Group align="center" gap={8} mb={rem(8)}>
                <IconIndexedPath value={0} width={16} height={16} />
                <Text lh="16px" size="md" fw={700}>
                  Điểm giao hàng
                </Text>
                <Box
                  ml={rem(8)}
                  px={rem(8)}
                  py={rem(2)}
                  bg="#F1F6FF"
                  style={{ borderRadius: rem(8) }}
                >
                  <Text
                    size="xs"
                    c="primary.6"
                    fw={600}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    Áp dụng cho tất cả các điểm giao
                  </Text>
                </Box>
              </Group>
              <Stack gap={4} mt={rem(4)}>
                {dropOffPath.map((DO, idx) => (
                  <Group key={idx} gap={8} align="flex-start">
                    <Box
                      w={rem(20)}
                      h={rem(20)}
                      bg="#F1F6FF"
                      style={{
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Text
                        size="xs"
                        c="primary.6"
                        fw={700}
                        style={{ lineHeight: rem(20) }}
                      >
                        {idx + 1}
                      </Text>
                    </Box>
                    <Text lineClamp={1} c="neutral.5" size="sm" fw={500}>
                      {DO.address}
                    </Text>
                  </Group>
                ))}
              </Stack>
            </Box>
          ) : null}

          <ModalWrapper
            title={'Hình thức bốc xếp'}
            isOpen={opened}
            onClose={handleCloseModal}
            optionStyle={{
              headerStyle: {
                minHeight: rem(56),
                borderBottom: `1px solid var(--mantine-color-neutral-0)`,
                paddingLeft: rem(16),
              },
              bodyStyle: {
                padding: 0,
              },
            }}
          >
            <>
              {editingType && tempConfig && (
                <Card withBorder>
                  <LoadingGoodsItem
                    type={editingType}
                    selectedHelperConfig={tempConfig}
                    title={
                      editingType === 'pickupHelperConfig'
                        ? 'Điểm lấy hàng'
                        : 'Điểm giao hàng'
                    }
                    distanceOptions={DISTANCE_OPTIONS}
                    onDistanceChange={(value) =>
                      handleTempDistanceChange(value)
                    }
                    checkboxOptions={CHECKBOX_OPTIONS}
                    onCheckboxChange={(optionCode) =>
                      handleTempCheckboxChange(optionCode)
                    }
                    isOptionDisabled={isOptionDisabled}
                    isMobile={true}
                    icon={
                      editingType === 'pickupHelperConfig' ? (
                        <Pickup width={16} height={16} />
                      ) : (
                        <Path width={16} height={16} />
                      )
                    }
                    onConfirm={handleConfirmModalConfig}
                  />
                </Card>
              )}
            </>
          </ModalWrapper>
        </>
      ) : (
        <>
          <Group
            bg={'white'}
            w="100%"
            align="center"
            justify="space-between"
            p={16}
            pos="sticky"
            top={0}
            left={0}
            styles={{
              root: {
                zIndex: 100,
                borderBottom: `1px solid var(--mantine-color-neutral-1)`,
              },
            }}
          >
            <Title order={3}>{request?.name_vi_vn ?? 'Dịch vụ bốc xếp'}</Title>
            <ActionIcon
              size={rem(24)}
              variant="transparent"
              onClick={() => context.closeModal(id)}
            >
              <Close color="black" width={24} height={24} />
            </ActionIcon>
          </Group>

          <Stack mb={rem(16)} px={rem(16)} py={rem(24)}>
            <Group w="100%" align="center" justify="space-between">
              <Title order={5}>Chọn hình thức bốc xếp</Title>
              <Button
                component="a"
                href="https://ahamove.com/dich-vu-boc-xep-ahamove"
                target="_blank"
                variant="transparent"
                c="secondary.5"
                size="sm"
                fw={700}
              >
                Xem hướng dẫn
              </Button>
            </Group>

            <Stack py="md" gap={16}>
              {pickupPath && (
                <Card withBorder>
                  <Controller
                    name="pickupHelperConfig"
                    control={control}
                    render={() => (
                      <LoadingGoodsItem
                        type="pickupHelperConfig"
                        title="Điểm lấy hàng"
                        distanceOptions={DISTANCE_OPTIONS}
                        selectedHelperConfig={pickupHelperConfig}
                        onDistanceChange={handleDistanceChange}
                        checkboxOptions={CHECKBOX_OPTIONS}
                        onCheckboxChange={handleCheckboxChange}
                        isOptionDisabled={isOptionDisabled}
                        icon={<Pickup width={16} height={16} />}
                      />
                    )}
                  />
                </Card>
              )}

              {dropOffPath.length > 0 && (
                <Card withBorder>
                  <Controller
                    name="deliveryHelperConfig"
                    control={control}
                    render={() => (
                      <LoadingGoodsItem
                        type="deliveryHelperConfig"
                        title="Điểm giao hàng"
                        description="Áp dụng cho tất cả các điểm giao hàng"
                        distanceOptions={DISTANCE_OPTIONS}
                        selectedHelperConfig={deliveryHelperConfig}
                        onDistanceChange={handleDistanceChange}
                        checkboxOptions={CHECKBOX_OPTIONS}
                        onCheckboxChange={handleCheckboxChange}
                        isOptionDisabled={isOptionDisabled}
                        icon={<Path width={16} height={16} />}
                      />
                    )}
                  />
                </Card>
              )}
            </Stack>
          </Stack>
        </>
      )}

      <Group
        justify="flex-end"
        mt="auto"
        gap={8}
        p="md"
        pos={{ base: 'fixed', sm: 'sticky' }}
        bottom={0}
        left={0}
        w="100%"
        bg="white"
        styles={{
          root: {
            zIndex: 100,
            borderTop: `1px solid var(--mantine-color-neutral-1)`,
          },
        }}
      >
        {isMobile ? (
          <ModalFooter>
            <Group w="100%" gap={rem(8)} justify="space-between" align="center">
              <Text c="neutral.6" size="sm">
                Chi phí bốc xếp:
              </Text>
              <Text size="md" fw={600} c="primary.5">
                {formatCurrency(calculatedFees())}
              </Text>
            </Group>
            <Button fullWidth size="md" mih={rem(48)} type="submit">
              {tActions('confirm')}
            </Button>
          </ModalFooter>
        ) : (
          <Group justify="flex-end" w="100%" align="center" p={0}>
            <Group gap={12}>
              <Text fw={600} c="neutral.6" size="sm">
                Chi phí bốc xếp:
              </Text>
              <Text fw={700} c="primary.6" size="xl">
                {formatCurrency(calculatedFees())}
              </Text>
            </Group>

            <Button
              size="md"
              miw={rem(120)}
              mih={rem(48)}
              type="submit"
              disabled={!isValid}
              style={{ whiteSpace: 'nowrap' }}
            >
              {tActions('confirm')}
            </Button>
          </Group>
        )}
      </Group>
    </form>
  );
}
